import { FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import * as React from 'react';
import classNames from 'classnames';
import { ViewportList } from 'react-viewport-list';

import { SelectOption } from '../../../@types/shared';
import Checkbox from '../../atoms/Checkbox';
import FilterControl, { FilterControlProps } from '../../atoms/FilterControl';
import Overlay from '../../atoms/Overlay';
import Radio from '../../atoms/Radio';
import Spinner from '../../atoms/Spinner';
import Tab from '../Tab';
import Text from '../../atoms/Text';
import Surface from '../../atoms/Surface';
import SearchBar from '../../molecules/SearchBar';

import styles from './TabbedMultipleSelectFilter.module.scss';

// Interfaces
// //////////////////////////

export interface TabsProps {
  name: string;
  label: string;
  type: 'radio' | 'checkbox';
  options: Array<SelectOption>;
}

export type Props = Omit<
  FilterControlProps,
  | 'value'
  | 'type'
  | 'readOnly'
  | 'prepend'
  | 'onClick'
  | 'onChange'
  | 'controlId'
> & {
  name: string;
  icon?: FontAwesomeIconProps['icon'];
  tabs: Array<TabsProps>;
  label?: string;
  helperText?: string;
  loading?: boolean;
  value: Record<string, string[]>;
  onOpen?: () => void;
  onChange?: (name: string, newValue: Array<string>) => void;
};

/**
 * <TabbedMultipleSelectFilter> component
 */
export const TabbedMultipleSelectFilter: React.FunctionComponent<Props> = ({
  className,
  style,
  name,
  label,
  icon,
  loading,
  value,
  tabs,
  helperText,
  onOpen,
  onChange,
  ...inputProps
}) => {
  /**
   * Build and return an object with a structure where each key represents a filter/tab and has an array of selected options
   */
  const parseValue = React.useCallback(
    (val: Props['value']) => {
      if (Object.keys(val).length === 0) {
        const templateValue: Record<string, string[]> = {};
        const filters = tabs.map((t) => t.name);
        filters.forEach((k) => {
          templateValue[k] = [''];
        });
        return templateValue;
      }
      return val;
    },
    [tabs],
  );

  const [show, setShow] = React.useState(false);
  const [target, setTarget] = React.useState<HTMLDivElement | null>(null);
  const [selected, setSelected] = React.useState(parseValue(value));
  const [search, setSearch] = React.useState('');
  const [inputRef, setInputRef] = React.useState<HTMLInputElement | null>(null);
  const [activeTab, setActiveTab] = React.useState<string | number>('0');
  const viewportRef = React.useRef<HTMLDivElement | null>(null);

  /**
   * Count every selected option in every tab to display as the FilterControl label
   */
  const displayValue = React.useMemo(
    () =>
      `(${Object.entries(selected).reduce(
        (acc, curr) => acc + curr[1].length,
        0,
      )})`,
    [selected],
  );

  /**
   * Handle change to selection when popover is open
   */
  const handleChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.type === 'radio') {
        setSelected((s) => ({
          ...s,
          [event.target.name]: [event.target.value],
        }));
      } else if (event.target.type === 'checkbox') {
        const selectedName = event.target.name.split('-')[0];
        // Reset selection if default (key: '') is selected
        if (event.target.value === '') {
          setSelected((s) => ({ ...s, [selectedName]: [''] }));
          return;
        }
        setSelected((s) =>
          event.target.checked
            ? {
                ...s,
                [selectedName]: [
                  ...s[selectedName].filter((e) => e !== ''),
                  event.target.value,
                ],
              }
            : {
                ...s,
                [selectedName]: s[selectedName].filter(
                  (e) => e !== event.target.value,
                ),
              },
        );
      }
    },
    [],
  );

  /**
   * Notify changes when overlay is closed
   */
  const handleOverlayHide = React.useCallback(() => {
    const filters = Object.keys(selected);
    if (onChange) filters.forEach((f) => onChange(f, selected[f]));
  }, [selected, onChange]);

  return (
    <div className={classNames(styles.wrapper, className)} style={style}>
      <FilterControl
        className="selector-control"
        ref={setTarget}
        prepend={icon}
        readOnly
        open={show}
        variant="outline"
        value={`${label} ${displayValue}` || ''}
        onClick={() => {
          setSelected(parseValue(value));
          setShow((s) => !s);
          if (show) handleOverlayHide();
          if (!show && onOpen) onOpen();
        }}
        {...inputProps}
      />

      {helperText && (
        <Text as="span" className="mt-2" size="sm1">
          {helperText}
        </Text>
      )}

      <Overlay
        className="selector-overlay"
        target={target}
        show={show}
        onHide={() => {
          handleOverlayHide();
          setShow(false);
        }}
        placement="bottom-start"
      >
        <Surface padding="sm" backgroundColor="grayLight3">
          {loading && <Spinner size="sm" />}

          {/* Render a radio option menu */}
          {!loading && (
            <Tab
              activeKey={activeTab}
              tabPaneStyle={{ padding: '0.5rem' }}
              onTabSelected={(k) => {
                if (inputRef) inputRef.value = '';
                setSearch('');
                setActiveTab(k);
              }}
            >
              {tabs.map((tab, i) => (
                <Tab.TabPane
                  key={tab.label}
                  tabKey={i.toString()}
                  title={tab.label}
                >
                  <SearchBar
                    ref={setInputRef}
                    className="mb-2"
                    placeholder="Pesquisar"
                    size="sm"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    showIcon={false}
                  />

                  <div className={styles['scroll-area']} ref={viewportRef}>
                    <ViewportList
                      viewportRef={viewportRef}
                      items={tab.options.filter(
                        (opt) =>
                          search === '' ||
                          opt.label
                            .toLowerCase()
                            .includes(search.toLowerCase()),
                      )}
                    >
                      {(opt, idx, arr) => {
                        if (tab.type === 'checkbox') {
                          return (
                            <Checkbox
                              key={opt.id}
                              className={classNames(styles.option, {
                                last: idx === arr.length - 1,
                              })}
                              name={`${tab.name}-${idx}`}
                              label={opt.label}
                              value={opt.id}
                              checked={selected[tab.name].includes(opt.id)}
                              onChange={handleChange}
                            />
                          );
                        }

                        return (
                          <Radio
                            key={opt.id}
                            className={classNames(styles.option, {
                              last: idx === arr.length - 1,
                            })}
                            name={tab.name}
                            label={opt.label}
                            value={opt.id}
                            checked={selected[tab.name].includes(opt.id)}
                            onChange={handleChange}
                          />
                        );
                      }}
                    </ViewportList>
                  </div>
                </Tab.TabPane>
              ))}
            </Tab>
          )}
        </Surface>
      </Overlay>
    </div>
  );
};

export default TabbedMultipleSelectFilter;
